import {
    useMutation,
    UseMutationResult,
    useQuery,
    UseQueryResult,
} from 'react-query';
import { AxiosResponse } from 'axios';
import {
    withHeaders,
    instance as axiosInstance,
} from 'core/util/axiosInstance';
import { useAlert } from 'core/contexts';
import { commonShowAlert } from 'lib';
import { getDefaultHeaders } from 'core/util/other';
import { RULE_SET } from 'pages/pipelines/pipelines-helper';

const BASE_RULE_SET_URL = '/metadata/datapipeline/steps/ruleset';

interface FetchRuleSetProps {
    id: string;
    token: string;
}

export interface RuleSetStepResponse {
    id: string;
    name: string;
    description: string;
    ruleIds: string[];
}

export interface PostRuleSetStepProps {
    name: string;
    description: string;
    ruleIds: string[];
}

interface CreateRuleSetStepProps {
    postData: PostRuleSetStepProps;
    token: string;
}

export interface PutRuleSetStepProps extends PostRuleSetStepProps {
    id: string;
}

interface UpdateRuleSetStepProps {
    putData: PutRuleSetStepProps;
    token: string;
}

interface UpdateRuleSetTrialRunProps {
    id: string;
    token: string;
    postData: {};
}

export const getRuleSetData = async (
    url: string,
    token: string
): Promise<RuleSetStepResponse> => {
    const headers = getDefaultHeaders(token);
    const response = await axiosInstance().get(url, withHeaders(headers));
    return response.data;
};

export const useRuleSet = (
    props: FetchRuleSetProps,
    type: string
): UseQueryResult<RuleSetStepResponse, Error> => {
    const { id, token } = props;
    const { showAlert, hideAlert, showErrorDetails } = useAlert();
    const url = `${BASE_RULE_SET_URL}/${id}`;

    return useQuery(['RuleSet', id], async () => getRuleSetData(url, token), {
        enabled: type === RULE_SET,
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to retrieve rule set data';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const postRuleSet = (
    props: CreateRuleSetStepProps
): Promise<AxiosResponse<RuleSetStepResponse, any>> => {
    const { postData, token } = props;
    const url = `${BASE_RULE_SET_URL}`;

    return axiosInstance().post(
        url,
        postData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const useCreateRuleSet = (): UseMutationResult<
    AxiosResponse<RuleSetStepResponse, any>,
    Error,
    CreateRuleSetStepProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(postRuleSet, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Step Created.');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to create step';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const putRuleSet = (
    props: UpdateRuleSetStepProps
): Promise<AxiosResponse<RuleSetStepResponse, any>> => {
    const { putData, token } = props;
    const url = `${BASE_RULE_SET_URL}/${putData.id}`;

    return axiosInstance().put(
        url,
        putData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const useUpdateRuleSet = (): UseMutationResult<
    AxiosResponse<RuleSetStepResponse, any>,
    Error,
    UpdateRuleSetStepProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(putRuleSet, {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Step Updated');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to update step';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const deleteRuleSet = (
    props: FetchRuleSetProps
): Promise<AxiosResponse<void, any>> => {
    const { id, token } = props;
    const url = `${BASE_RULE_SET_URL}/${id}`;

    return axiosInstance().delete(url, withHeaders(getDefaultHeaders(token)));
};

export const useDeleteRuleSet = (): UseMutationResult<
    AxiosResponse<any, any>,
    Error,
    FetchRuleSetProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation((props: FetchRuleSetProps) => deleteRuleSet(props), {
        onSuccess: () => {
            commonShowAlert(showAlert, hideAlert, 'success', 'Step Deleted');
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to delete step';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const postRuleSetTrialRun = (
    props: UpdateRuleSetTrialRunProps
): Promise<AxiosResponse> => {
    const { id, postData, token } = props;
    const url = `${BASE_RULE_SET_URL}/${id}/trial-run`;

    return axiosInstance().post(
        url,
        postData,
        withHeaders(getDefaultHeaders(token))
    );
};

export const useRuleSetTrialRun = (): UseMutationResult<
    AxiosResponse<any, any>,
    any,
    UpdateRuleSetTrialRunProps,
    unknown
> => {
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(postRuleSetTrialRun, {
        onSuccess: () => {
            commonShowAlert(
                showAlert,
                hideAlert,
                'success',
                'Trial Run Created'
            );
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to create trial run';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};
